import Axios from "axios";
import axios from "./axios";

/**
 * Media API - enterprise object storage via the backend (MinIO).
 *
 * Replaces the previous direct-to-Firebase upload flow. The browser asks the
 * backend for a short-lived presigned PUT URL, uploads the bytes straight to
 * MinIO (no proxying through the API), then receives a presigned GET URL.
 *
 * Backend endpoints (cdnktapi):
 *   POST   /media/presign-upload   -> { upload_url, headers, download_url_after, key, ... }
 *   POST   /media/presign-download -> { url }
 *   DELETE /media                  -> deletes object(s)
 */

export interface PresignUploadResponse {
  key: string;
  bucket: string;
  upload_url: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_in: number;
  download_url_after: string;
}

export interface UploadResult {
  /** Presigned GET URL to store/display. */
  url: string;
  /** Object key inside the bucket (useful for later deletion). */
  key: string;
}

/**
 * Maps a logical area to the storage directory used by the backend.
 *
 * Directories under "public/" are the shared catalogue namespace: the bucket
 * grants anonymous read ONLY on that prefix, and the backend does not tenant-
 * scope it. Use it for anything whose URL is persisted in the database and has
 * to keep working forever (brand logos, pub artwork). Everything else is
 * tenant-scoped ("u-<id>/...") and served through short-lived signed URLs.
 */
export type StorageDirectory =
  // Shared catalogue — permanent, anonymously readable URLs.
  | "public/brand-logos"
  | "public/pub"
  | "public/company-logos"
  | "public/sign"
  // Tenant-scoped — private, served via presigned GET.
  | "vehicle/picture"
  | "strongbox"
  | "license"
  | "user/identity"
  | "user/avatar"
  | "sinistre"
  | "sign"
  | "company"
  | "misc";

/**
 * Request a presigned upload descriptor from the backend.
 */
async function requestPresignUpload(params: {
  mimeType: string;
  directory?: StorageDirectory;
  filename?: string;
}): Promise<PresignUploadResponse> {
  const { data } = await axios.post<PresignUploadResponse>(
    "/media/presign-upload",
    {
      mime_type: params.mimeType,
      directory: params.directory ?? "misc",
      filename: params.filename,
    }
  );
  return data;
}

/**
 * Upload a File/Blob to MinIO via a presigned PUT URL.
 *
 * The PUT goes directly to MinIO, NOT through the API, so large files never
 * buffer in the backend. A bare Axios instance is used for the PUT so app
 * auth interceptors/headers are not attached to the MinIO request.
 *
 * @returns The presigned download URL and the object key.
 */
export async function uploadToStorage(
  file: File | Blob,
  options?: { directory?: StorageDirectory; filename?: string }
): Promise<UploadResult> {
  const mimeType = (file as File).type || "application/octet-stream";
  const filename = options?.filename ?? (file as File).name ?? undefined;

  const presigned = await requestPresignUpload({
    mimeType,
    directory: options?.directory,
    filename,
  });

  try {
    await Axios.put(presigned.upload_url, file, {
      headers: {
        ...presigned.headers,
        "Content-Type": mimeType,
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  } catch (err: any) {
    // A CORS rejection surfaces here as a "Network Error" with no response:
    // the browser blocks the response before axios ever sees a status. Give
    // that case a message an operator can act on instead of a bare failure.
    if (!err?.response) {
      const host = (() => {
        try {
          return new URL(presigned.upload_url).origin;
        } catch {
          return presigned.upload_url;
        }
      })();
      throw new Error(
        `Upload blocked before reaching storage (${host}). This is normally a CORS ` +
          `or TLS problem: check that ${window.location.origin} is listed in ` +
          `MINIO_API_CORS_ALLOW_ORIGIN on the storage server.`
      );
    }
    throw err;
  }

  return { url: presigned.download_url_after, key: presigned.key };
}

/**
 * Ask the backend for a fresh presigned GET URL for an existing object key.
 * Presigned URLs expire, so views that display stored files long after upload
 * should refresh the URL via this call rather than persisting a stale link.
 */
export async function refreshDownloadUrl(
  key: string,
  expiresIn = 900
): Promise<string> {
  const { data } = await axios.post<{ key: string; url: string }>(
    "/media/presign-download",
    { key, expires_in: expiresIn }
  );
  return data.url;
}

/**
 * Delete one or more objects from storage.
 */
export async function deleteFromStorage(
  keys: string | string[]
): Promise<void> {
  const payload = Array.isArray(keys) ? { keys } : { key: keys };
  await axios.delete("/media", { data: payload });
}
